/**
 * Composable for config on batches
 */
/* UTILS */
import { computed, type Ref } from 'vue';

import Reporter from '@/api/clients/reporter/reporter';
import { useDeleteBatchMutation, useRetryBatchMutation } from '@/api/vue-query/batches/batches.mutations';
import { t } from '@/config/i18n';
import { MAX_ERRORS_BATCHES, supportEmail } from '@/enums/constants';
/* HELPERS */
import type { Batch, BatchActionType } from '@/types/batches/batches.types';
import type { VuetifyColorName } from '@/types/colors/colors.types';
import type { TextsTypes } from '@/types/texts/texts.types';

type BatchStatus = 'processing' | 'finished' | 'failed' | 'failedWithoutRetry' | 'retrying';
type ActionConfig = { label: string; icon?: string; action: () => void; loading: boolean };
type StatusConfigData = {
  actions: ActionConfig[];
  info: {
    text: string;
    date: string;
    hour: string;
    textSize?: TextsTypes;
    icon?: string;
    color?: VuetifyColorName;
    copyData?: string;
    showProgress?: boolean;
    showLoading?: boolean;
    link?: { text: string; action: () => void } | undefined;
  };
};

type BatchActionConfigData = {
  label: string;
};

export function useBatchesConfig(batch: Ref<Batch>, emits) {
  const errorsCount = computed(() => batch.value.nb_failed_operations);

  /* API */
  const { mutate: deleteBatch, isPending: deletingBatch } = useDeleteBatchMutation();
  const { mutate: retryBatch, isPending: retryingBatch } = useRetryBatchMutation();

  const actionTypesConfig = computed<Record<BatchActionType, BatchActionConfigData>>(() => {
    return {
      send_reminders: {
        label: t('recruiter_network.queue.panel.card.network.reminder.title')
      },
      send_results: {
        label: t('recruiter_network.queue.panel.card.network.results.title')
      },
      campaign_candidate: {
        label: t('recruiter_network.queue.panel.card.campaign.user_update.title')
      },
      campaign_delete_candidate: {
        label: t('recruiter_network.queue.panel.card.campaign.remove_user.title')
      },
      add_users_to_group: {
        label: t('recruiter_network.queue.panel.card.network.add_group.title')
      },
      add_users_to_campaign: {
        label: t('recruiter_network.queue.panel.card.network.add_campaign.title')
      },
      add_users_to_sharepools: {
        label: t('recruiter_network.queue.panel.card.invite.title')
      },
      share_profiles_to_managers: {
        label: t('recruiter_network.queue.panel.card.network.share_profile.title')
      },
      mark_as_employees: {
        label: t('recruiter_network.queue.panel.card.network.employee_add.title')
      },
      unmark_as_employees: {
        label: t('recruiter_network.queue.panel.card.network.employee_remove.title')
      },
      campaign_sync: {
        label: t('recruiter_network.queue.panel.card.result.title')
      },
      invite_contacts: {
        label: t('recruiter_network.queue.panel.card.invite.title')
      },
      delete_users_from_groups: {
        label: t('recruiter_network.queue.panel.card.network.remove_group.title')
      },
      delete_users_from_sharepools: {
        label: t('recruiter_network.queue.panel.card.network.remove_network.title')
      },
      add_group_to_campaign: {
        label: t('recruiter_network.queue.panel.card.network.add_campaign.title')
      },
      invite_managers: {
        label: t('recruiter_network.queue.panel.card.manager.add_network.title')
      },
      delete_managers: {
        label: t('recruiter_network.queue.panel.card.manager.remove_network.title')
      },
      reminder_managers: {
        label: t('recruiter_network.queue.panel.card.manager.reminder.title')
      },
      delete_competency_framework_users: {
        label: t('recruiter_network.queue.panel.card.competency_framework.remove_employee.title')
      },
      create_competency_framework_users: {
        label: t('recruiter_network.queue.panel.card.competency_framework.add_employee.title')
      },
      delete_chatbot_history: {
        label: 'recruiter_network.queue.panel.card.chatbot.title'
      }
    };
  });

  /* CONFIG FOR BATCH STATUS */
  /* ACTION BUTTONS DEFINITION */

  const REMOVE_ACTION = computed<ActionConfig>(() => {
    return {
      icon: 'fas fa-trash',
      label: t('general.delete'),
      loading: deletingBatch.value,
      action: () => deleteBatch(batch.value)
    };
  });

  const RETRY_ACTION = computed<ActionConfig>(() => {
    return {
      icon: 'arrow-rotate-right',
      label: t('general.retry'),
      loading: retryingBatch.value,
      action: () => retryBatch(batch.value)
    };
  });

  const isFullyFailed = computed(() => batch.value.nb_failed_operations === batch.value.nb_total_operations);

  const statusConfig = computed<Record<BatchStatus, StatusConfigData>>(() => {
    return {
      processing: {
        actions: [REMOVE_ACTION.value],
        info: {
          text: t('recruiter_network.queue.panel.card.progress', {
            done: batch.value.nb_total_operations - batch.value.nb_pending_operations,
            total: batch.value.nb_total_operations
          }),
          date: t('general.date.short', { value: batch.value.created_at * 1000 }),
          hour: t('recruiter_network.queue.panel.request.in_progress', { queue: batch.value.created_at * 1000 }),
          showProgress: true
        }
      },
      retrying: {
        actions: [REMOVE_ACTION.value],
        info: {
          text: t('recruiter_network.queue.panel.card.retry'),
          date: t('general.date.short', { value: batch.value.created_at * 1000 }),
          hour: t('recruiter_network.queue.panel.request.in_progress', { queue: batch.value.created_at * 1000 }),
          showLoading: true
        }
      },
      finished: {
        actions: [REMOVE_ACTION.value],
        info: {
          icon: 'fa-check-circle',
          color: 'success' as VuetifyColorName,
          text: t('recruiter_network.queue.panel.card.success'),
          date: t('general.date.short', { value: Number(batch.value.finished_at) * 1000 }),
          hour: t('recruiter_network.queue.panel.request.end', { queue: Number(batch.value.finished_at) * 1000 })
        }
      },
      failed: {
        actions: [RETRY_ACTION.value, REMOVE_ACTION.value],
        info: {
          icon: isFullyFailed.value ? 'fa-times-circle' : 'fa-exclamation-triangle',
          color: (isFullyFailed.value ? 'error' : 'warning') as VuetifyColorName,
          text: t('recruiter_network.queue.panel.card.fail.action', { fail: errorsCount.value }),
          link:
            errorsCount.value < MAX_ERRORS_BATCHES && !!batch.value.errors_excerpt.flat().length
              ? {
                  text: t('recruiter_network.queue.panel.card.fail.list'),
                  action: () => {
                    emits('show-errors', { ...batch.value, label: actionTypesConfig.value[batch.value.type].label });

                    // REPORTER
                    Reporter().reportData('view_batch_errors');
                  }
                }
              : undefined,
          date: t('general.date.short', { value: Number(batch.value.finished_at) * 1000 }),
          hour: t('recruiter_network.queue.panel.request.end', { queue: Number(batch.value.finished_at) * 1000 })
        }
      },
      failedWithoutRetry: {
        actions: [REMOVE_ACTION.value],
        info: {
          icon: isFullyFailed.value ? 'fa-times-circle' : 'fa-exclamation-triangle',
          color: (isFullyFailed.value ? 'error' : 'warning') as VuetifyColorName,
          text: t('recruiter_network.queue.panel.card.fail.many_1'),
          copyData: supportEmail,
          date: t('general.date.short', { value: Number(batch.value.finished_at) * 1000 }),
          hour: t('recruiter_network.queue.panel.request.end', { queue: Number(batch.value.finished_at) * 1000 })
        }
      }
    };
  });

  // Use given batch data to retrieve the proper batch status
  const batchStatus = computed((): BatchStatus => {
    let status: BatchStatus = 'processing';
    if (!batch.value.finished_at && batch.value.nb_retries > 0) {
      status = 'retrying';
    } else if (batch.value.finished_at) {
      // If batch is finished, set status according to fail count + whether or not max retries was reached
      if (batch.value.nb_failed_operations) {
        status = !batch.value.is_retriable ? 'failedWithoutRetry' : 'failed';
      } else {
        status = 'finished';
      }
    }
    return status;
  });

  const statusActionButtons = computed(() => statusConfig.value[batchStatus.value].actions); // action buttons to display according to batch status
  const statusInfo = computed(() => statusConfig.value[batchStatus.value].info); // Info to display next to the buttons according to batch status
  const actionTypeConfig = computed(() => actionTypesConfig.value[batch.value.type]);
  const isLoading = computed(() => deletingBatch.value || retryingBatch.value);

  return { batchStatus, statusActionButtons, statusInfo, actionTypeConfig, isLoading };
}
